import { type Currency, RecordType, RecordUnit } from '@kanbu/schema/enums';
import { formatUtils } from '@kanbu/shared';
import { Trans, useLingui } from '@lingui/react/macro';
import { Table } from '@utima/ui';

import { useEnumTranslations } from '@/hooks';
import { trpc } from '@/services/trpc';

export interface ThreadCostsProps {
  threadId: string;
  currency: Currency;
}

export function ThreadCosts({ threadId, currency }: ThreadCostsProps) {
  const { t } = useLingui();
  const { recordUnit } = useEnumTranslations();
  const { data, isLoading } = trpc.threads.getThreadCosts.useQuery({
    id: threadId,
  });

  const totalCost = data?.reduce((acc, item) => acc + item.totalCost, 0) || 0;

  const formatUnits = (units: number, unitType: RecordUnit) => {
    switch (unitType) {
      case RecordUnit.Tokens:
      case RecordUnit.Characters:
        return formatUtils.number(units);
      case RecordUnit.Seconds:
        return formatUtils.duration(units);
      case RecordUnit.Credits:
        return formatUtils.number(units);

      default:
        return formatUtils.number(units);
    }
  };

  return (
    <div className='space-y-3'>
      <div className='flex flex-col gap-0.5'>
        <h4 className='text-sm font-semibold leading-none tracking-tight'>
          <Trans>Costs breakdown</Trans>
        </h4>
        <p className='text-xs text-muted-fg'>
          <Trans>
            Detailed breakdown of costs associated with this thread.
          </Trans>
        </p>
      </div>

      {isLoading ? (
        <div className='text-sm'>
          <Trans>Loading</Trans>
        </div>
      ) : !data || data.length === 0 ? (
        <p className='text-sm text-muted-foreground'>
          <Trans>No usage data available for this thread.</Trans>
        </p>
      ) : (
        <>
          <Table.Root>
            <Table.Head>
              <Table.Row className='[&>th]:py-1.5'>
                <Table.HCol>
                  <Trans>Type</Trans>
                </Table.HCol>
                <Table.HCol className='text-right'>
                  <Trans>Usage</Trans>
                </Table.HCol>
                <Table.HCol className='text-right'>
                  <Trans>Cost</Trans>
                </Table.HCol>
              </Table.Row>
            </Table.Head>
            <Table.Body>
              {data.map((item, index) => {
                const isLlm = item.type === RecordType.Llm;
                const unitLabel = recordUnit[item.unitType].toLowerCase();

                return (
                  <Table.Row
                    key={`${item.type}-${item.unitType}-${index}`}
                    className='[&>td]:py-1.5'
                  >
                    <Table.Col className='align-top text-sm font-medium'>
                      {item.type}
                    </Table.Col>
                    <Table.Col className='align-top text-right text-sm tabular-nums'>
                      {isLlm ? (
                        <div className='flex flex-col items-end leading-tight'>
                          <span>
                            {formatUtils.number(item.inputTokens ?? 0)}{' '}
                            <span className='text-xs text-muted-fg'>
                              {t`in`}
                            </span>
                          </span>
                          <span>
                            {formatUtils.number(item.outputTokens ?? 0)}{' '}
                            <span className='text-xs text-muted-fg'>
                              {t`out`}
                            </span>
                          </span>
                        </div>
                      ) : (
                        <span>
                          {formatUnits(item.totalUnits, item.unitType)}{' '}
                          <span className='text-xs text-muted-fg'>
                            ({unitLabel})
                          </span>
                        </span>
                      )}
                    </Table.Col>
                    <Table.Col className='align-top text-right text-sm font-medium tabular-nums'>
                      {isLlm ? (
                        <div className='flex flex-col items-end leading-tight'>
                          <span>
                            {formatUtils.credits(item.totalCost, currency)}
                          </span>
                          <span className='text-xs font-normal text-muted-fg'>
                            {formatUtils.credits(item.inputCost ?? 0, currency)}{' '}
                            {t`in`}
                          </span>
                          <span className='text-xs font-normal text-muted-fg'>
                            {formatUtils.credits(
                              item.outputCost ?? 0,
                              currency,
                            )}{' '}
                            {t`out`}
                          </span>
                        </div>
                      ) : (
                        formatUtils.credits(item.totalCost, currency)
                      )}
                    </Table.Col>
                  </Table.Row>
                );
              })}
            </Table.Body>
          </Table.Root>
          <div className='flex justify-end text-sm font-semibold'>
            <span className='mr-2 text-muted-fg'>{t`Total`}:</span>
            <span>{formatUtils.credits(totalCost, currency)}</span>
          </div>
        </>
      )}
    </div>
  );
}
